/**
 * useOfficePermissions — office-app RBAC gate.
 *
 * Thin wrapper over `useUserPermissions` (the DB-backed union of every role's
 * permissions, fixed in card A1) that folds in the admin bypass. Admins
 * (`selectIsAdmin`) always pass every check; everyone else is checked against
 * their permission union.
 *
 * Use this for client-side gating of office navigation (card A3) and
 * create/edit/delete controls (card A4). Server-side enforcement is a separate
 * concern (card A5) — this is purely a UX gate so users don't see actions they
 * can't perform.
 *
 * @example
 * ```tsx
 * const { can, isLoading } = useOfficePermissions();
 * if (isLoading) return null;          // avoid flashing "no access"
 * if (can('jobs:create')) { ...show Add Job... }
 * ```
 */
import { useMemo } from 'react';

import { useAppSelector } from '../lib/reduxHooks.js';
import { selectIsAdmin } from '../store/slices/auth.js';

import { useUserPermissions } from './useUserPermissions.js';

export function useOfficePermissions() {
  const isAdmin = useAppSelector(selectIsAdmin);
  const { hasPermission, permissions, isLoading } = useUserPermissions();

  // `can` returns true for admins unconditionally, otherwise defers to the
  // user's permission union. Memoized so consumers get a stable reference.
  const can = useMemo(
    () => (permName: string) => isAdmin || hasPermission(permName),
    [isAdmin, hasPermission]
  );

  // True when the current user holds at least one of the given permissions
  // (admins always). Handy for menu items that map to several read scopes.
  const canAny = useMemo(
    () => (permNames: string[]) => isAdmin || permNames.some((p) => hasPermission(p)),
    [isAdmin, hasPermission]
  );

  return { can, canAny, isAdmin, permissions, isLoading };
}
